import { useState } from "react";
import { CalcCard, NumField, ResultRow, PrimaryButton, Section, SaveButton } from "@/components/CalcUI";
import { runCalc } from "@/lib/api";
import { toast } from "sonner";

export default function CollapseBurst() {
  const [burst, setBurst] = useState({ od: "5.5", wall_t: "0.361", yield_strength: "80000", safety: "0.875" });
  const [bOut, setBOut] = useState(null);

  const [coll, setColl] = useState({ od: "5.5", wall_t: "0.361", yield_strength: "80000" });
  const [cOut, setCOut] = useState(null);

  const run = async (calc, inp, setter) => {
    try {
      const r = await runCalc(calc, Object.fromEntries(Object.entries(inp).map(([k, v]) => [k, parseFloat(v)])));
      setter(r);
    } catch (e) {
      toast.error("Invalid inputs");
    }
  };

  return (
    <div data-testid="collapse-burst-page">
      <Section
        title="Collapse / Burst"
        subtitle="API 5C3 yield-strength collapse and Barlow burst pressure. Yield-strength collapse is valid for low D/t ratios (thick-wall pipe). For higher D/t use plastic/transition regimes."
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <CalcCard testid="burst-card" title="Burst (Barlow)" formula="P = 2·Yp·t·SF / D">
            <div className="grid grid-cols-2 gap-3">
              <NumField testid="bp-od" label="OD" unit="in" value={burst.od} onChange={(v) => setBurst({ ...burst, od: v })} />
              <NumField testid="bp-t" label="Wall" unit="in" value={burst.wall_t} onChange={(v) => setBurst({ ...burst, wall_t: v })} />
              <NumField testid="bp-y" label="Yield" unit="psi" value={burst.yield_strength} onChange={(v) => setBurst({ ...burst, yield_strength: v })} />
              <NumField testid="bp-sf" label="Safety" unit="—" value={burst.safety} onChange={(v) => setBurst({ ...burst, safety: v })} />
            </div>
            <div className="mt-4 flex justify-between">
              <PrimaryButton testid="bp-run" onClick={() => run("burst_barlow", burst, setBOut)}>Calculate</PrimaryButton>
              <SaveButton getPayload={() => bOut && { category: "Collapse/Burst", calculator: "burst_barlow", inputs: burst, outputs: bOut, units: "field", default_name: "Burst" }} />
            </div>
            {bOut && <div className="mt-4 border-t border-[#334155] pt-3"><ResultRow label="Burst rating" value={bOut.burst_psi} unit="psi" emphasis /></div>}
          </CalcCard>

          <CalcCard testid="collapse-card" title="Collapse (Yield-Strength, API 5C3)" formula="Pcy = 2·Yp·((D/t)−1)/(D/t)²">
            <div className="grid grid-cols-3 gap-3">
              <NumField testid="cp-od" label="OD" unit="in" value={coll.od} onChange={(v) => setColl({ ...coll, od: v })} />
              <NumField testid="cp-t" label="Wall" unit="in" value={coll.wall_t} onChange={(v) => setColl({ ...coll, wall_t: v })} />
              <NumField testid="cp-y" label="Yield" unit="psi" value={coll.yield_strength} onChange={(v) => setColl({ ...coll, yield_strength: v })} />
            </div>
            <div className="mt-4"><PrimaryButton testid="cp-run" onClick={() => run("collapse_yield", coll, setCOut)}>Calculate</PrimaryButton></div>
            {cOut && (
              <div className="mt-4 border-t border-[#334155] pt-3">
                <ResultRow label="Collapse rating" value={cOut.collapse_psi} unit="psi" emphasis />
                <ResultRow label="D/t" value={cOut.d_over_t} unit="—" />
                {cOut.d_over_t > 15 && (
                  <div className="text-[11px] mono text-[#fbbf24] mt-2 border-l-2 border-[#f59e0b] pl-2">
                    D/t &gt; 15 — yield-strength regime may under-estimate. Check plastic/transition/elastic ranges.
                  </div>
                )}
              </div>
            )}
          </CalcCard>
        </div>
      </Section>
    </div>
  );
}
